import { create } from 'zustand';
import {
  createJSONStorage,
  persist,
  type StateStorage,
} from 'zustand/middleware';

export enum UserRole {
  USER = 'user',
  ADMIN = 'admin',
  SUPER_ADMIN = 'super-admin',
}

export interface User {
  id: string;
  email: string;
  isVerified: boolean;
  status: 'active' | 'inactive';
  tenantId: string;
  fullName: string | null;
  userName: string | null;
  profilePicture?: string | null;
  areaOfInterest?: string[] | null;
  role?: UserRole;
}

interface AuthState {
  token: string | null;
  user: User | null;
  setToken: (token: string) => void;
  setUser: (user: User) => void;
  clearAuth: () => void;
  storageType: 'local' | 'session';
  setStorageType: (type: 'local' | 'session') => void;

  refreshToken: string | null;
  setRefreshToken: (refreshToken: string) => void;

  isHydrated: boolean; // <-- NEW
  setIsHydrated: (isHydrated: boolean) => void; // <-- NEW
}

const createCustomStorage = (): StateStorage => {
  const getStorageType = () => {
    try {
      const storageType = JSON.parse(
        localStorage.getItem('ihs-storage-type') || 'local'
      );
      return storageType === 'local' ? 'local' : 'session';
    } catch (e) {
      return 'local';
    }
  };

  const storage = {
    getItem: (name: string) => {
      const storageType = getStorageType();
      const storageObj =
        storageType === 'local' ? localStorage : sessionStorage;
      const value = storageObj.getItem(name);
      return value;
    },
    setItem: (name: string, value: string) => {
      const storageType = getStorageType();
      const storageObj =
        storageType === 'local' ? localStorage : sessionStorage;
      storageObj.setItem(name, value);
    },
    removeItem: (name: string) => {
      const storageType = getStorageType();
      const storageObj =
        storageType === 'local' ? localStorage : sessionStorage;
      storageObj.removeItem(name);
    },
  };

  return storage;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      refreshToken: null,
      storageType: 'local',
      setToken: (token: string) => set({ token }),
      setRefreshToken: (refreshToken: string) => set({ refreshToken }),
      setUser: (user: User) => set({ user }),
      setStorageType: (type: 'local' | 'session') => {
        localStorage.setItem('ihs-storage-type', JSON.stringify(type));
        set({ storageType: type });
      },
      clearAuth: () => {
        set({ token: null, user: null, refreshToken: null });
        localStorage.setItem('ihs-storage-type', JSON.stringify('local'));
        set({ storageType: 'local' });
      },
      isHydrated: false, // <-- NEW
      setIsHydrated: (isHydrated: boolean) => set({ isHydrated }), // <-- NEW
    }),
    {
      name: 'ihs-auth-storage',
      storage: createJSONStorage(() => createCustomStorage()),
      onRehydrateStorage: () => (state) => {
        state?.setIsHydrated(true); // <-- Trigger when hydration completes
      },
    }
  )
);
